/**
 * Typed calls for every invoicing route of Phase 1 (CONTRACT.md §4). Shapes
 * come from invoicing.types.ts, generated from docs/invoicing/openapi.json —
 * regenerate with `npm run gen:invoicing-types` when the contract changes.
 */
import { authedPosApi } from './authedPosApi';
import type { components } from './invoicing.types';

type S = components['schemas'];
export type InvoiceDoc = S['InvoiceDoc'] & {
  lastDelivery?: { channel: string; state: 'SENT' | 'DELIVERED' | 'READ' | 'FAILED' | 'SHARED'; at: number; messageId?: string };
};
export type InvoiceListItem = S['InvoiceListItem'];
export type DocumentInput = S['DocumentInput'];
export type LineInput = S['LineInput'];
export type Line = S['Line'];
export type Charge = S['Charge'];
export type Discount = S['Discount'];
export type Payment = S['Payment'];
export type InvoiceEvent = S['InvoiceEvent'];
export type InvoiceSettings = S['InvoiceSettings'];
export type DisplayStatus = S['DisplayStatus'];
export type PaymentMethod = S['PaymentMethod'];
export type PaymentTerms = S['PaymentTerms'];
export type CustomerSnapshot = S['CustomerSnapshot'];
export type CustomerBalance = S['CustomerBalance'];
export type Collections = S['Collections'];
export type FromOrderMode = 'PAID' | 'ON_ACCOUNT';
export interface FromOrderResult { document: InvoiceDoc; creditWarning?: { outstandingMinor: number; creditLimitMinor: number } }
export interface CreditSales {
  totalMinor: number;
  outstandingMinor: number;
  count: number;
  items: { docId: string; number: string; orderId?: string; orderReceiptId?: string; customerName?: string; clientId?: string; issueDate: string; totalMinor: number; balanceMinor: number }[];
}
export interface CollectionsResult { closeCashId?: string; from?: string; to?: string; collections: Collections; creditSales?: CreditSales }

export interface ListSummary {
  outstandingMinor: number;
  overdueMinor: number;
  overdueCount: number;
  collectedThisMonthMinor: number;
  averageDaysToPay: number | null;
  pendingApprovalCount: number;
  pendingApprovalMinor: number;
}
export interface ListResult {
  items: InvoiceListItem[];
  nextCursor: string | null;
  counts: Record<string, number>;
  summary: ListSummary;
}
export interface ListQuery {
  docType?: 'INVOICE' | 'QUOTATION';
  tab?: string;
  clientId?: string;
  from?: string;
  to?: string;
  dueFrom?: string;
  dueTo?: string;
  minTotalMinor?: number;
  maxTotalMinor?: number;
  createdBy?: string;
  q?: string;
  limit?: number;
  cursor?: string;
}
export interface RelatedDoc { docId: string; docType: string; number: string | null; totalMinor: number; shareUrl?: string }
export interface DocumentDetail {
  document: InvoiceDoc;
  payments: Payment[];
  events: InvoiceEvent[];
  messages: MessageRow[];
  related: { parent?: RelatedDoc; children: RelatedDoc[] };
}
export interface PaymentInput {
  amountMinor: number;
  paidOn: string;
  method: PaymentMethod;
  reference?: string;
  chequeNo?: string;
  chequeBank?: string;
  note?: string;
}
export type CreditNoteInput =
  | { reason: string; reasonCode?: string; lines: { lineId: string; quantity: number }[] }
  | { reason: string; reasonCode?: string; amountMinor: number };

export interface ShareMessage {
  text: string;
  subject?: string;
  link: string;
  templateKey: string;
  language: 'en' | 'si' | 'ta';
  sms?: { encoding: 'GSM7' | 'UCS2'; characters: number; segments: number };
  to: { phoneE164?: string; email?: string };
}
export type ManualChannel = 'MANUAL_WHATSAPP' | 'MANUAL_SMS' | 'MANUAL_EMAIL' | 'COPY_LINK' | 'COPY_MESSAGE' | 'QR_DOWNLOAD';
export type PendingClaim = Payment & { slipUrl?: string; payerName?: string; possibleDuplicate?: boolean; document: InvoiceListItem & { balanceMinor: number } };
export type RejectCode = 'NOT_RECEIVED' | 'WRONG_AMOUNT' | 'DUPLICATE' | 'OTHER';
/** Built-in message texts: templateKey → channel → language → text (EMAIL: { subject, text }). */
export type TemplateDefaults = Record<string, Record<string, Record<string, string | { subject: string; text: string }>>>;

export type Channel = 'EMAIL' | 'SMS' | 'WHATSAPP';
export interface SendRequest {
  channels: Channel[];
  to: { email?: string; phone?: string };
  templateKey?: string;
  language?: 'en' | 'si' | 'ta';
  customText?: string;
  attachPdf?: boolean;
}
export interface SendResult {
  messages: { messageId: string; channel: Channel; to: string; status: string; creditCost: number }[];
  creditsUsed: number;
  creditsLeft: number | null;
  error?: { code: string; message: string; details?: any };
}
export interface MessageRow {
  messageId: string; docId: string; docNumber?: string; channel: Channel; to: string; templateKey: string; language: string;
  status: 'QUEUED' | 'SENDING' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED' | 'BOUNCED'; error?: string; creditCost: number;
  createdAt: number; updatedAt: number; sentAt?: number; deliveredAt?: number; readAt?: number;
}
export interface Credits { period: string; allowance: number; used: number; purchasedBalance: number; available: number; costs: { EMAIL: number; WHATSAPP: number; SMS_SEGMENT: number }; channels: Record<Channel, boolean> }

export type Recurring = S['Recurring'] & {
  shopId?: string; template?: any; lastRunDate?: string; lastDocId?: string; createdAt?: number; updatedAt?: number;
};
export interface RecurringInput {
  fromDocId?: string;
  name: string;
  frequency: 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
  interval?: number;
  startDate: string;
  end: { type: 'NEVER' } | { type: 'DATE'; date: string } | { type: 'COUNT'; count: number };
  mode: 'DRAFT' | 'ISSUE_AND_SEND';
  channels: Channel[];
}
export interface StatementEntry { date: string; kind: 'INVOICE' | 'PAYMENT' | 'CREDIT_NOTE'; docId: string; number: string | null; description?: string; debitMinor: number; creditMinor: number; balanceMinor: number }
export interface Statement {
  customer: { clientId: string; name: string; companyName?: string };
  from: string; to: string;
  openingBalanceMinor: number; closingBalanceMinor: number;
  entries: StatementEntry[];
  totals?: { invoicedMinor: number; paidMinor: number; creditedMinor: number };
  pdfUrl?: string; shareUrl?: string;
}

const doc = (id: string) => `/invoices/${encodeURIComponent(id)}`;
const newKey = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const InvoiceRepository = {
  list: (shopId: string, query: ListQuery = {}) =>
    authedPosApi<ListResult>(shopId, '/invoices', { query: query as any }),

  /** Fetches every page; used by CSV export. */
  async listAll(shopId: string, query: ListQuery = {}): Promise<InvoiceListItem[]> {
    const items: InvoiceListItem[] = [];
    let cursor: string | undefined;
    do {
      const page = await InvoiceRepository.list(shopId, { ...query, limit: 200, cursor });
      items.push(...page.items);
      cursor = page.nextCursor ?? undefined;
    } while (cursor);
    return items;
  },

  get: (shopId: string, docId: string) => authedPosApi<DocumentDetail>(shopId, doc(docId)),

  create: (shopId: string, input: DocumentInput, idempotencyKey: string = newKey()) =>
    authedPosApi<{ document: InvoiceDoc }>(shopId, '/invoices', { method: 'POST', body: input, idempotencyKey }),

  update: (shopId: string, docId: string, input: Partial<DocumentInput>) =>
    authedPosApi<{ document: InvoiceDoc }>(shopId, doc(docId), { method: 'PUT', body: input }),

  remove: (shopId: string, docId: string) => authedPosApi<null>(shopId, doc(docId), { method: 'DELETE' }),

  /** Issue; with `send`, also send by Netlise (the invoice is issued even if sending fails — see `send.error`). */
  issue: (shopId: string, docId: string, send?: SendRequest, idempotencyKey: string = newKey()) =>
    authedPosApi<{ document: InvoiceDoc; send?: SendResult }>(shopId, `${doc(docId)}/issue`, { method: 'POST', body: send ? { send } : {}, idempotencyKey }),

  send: (shopId: string, docId: string, request: SendRequest, idempotencyKey: string = newKey()) =>
    authedPosApi<SendResult>(shopId, `${doc(docId)}/send`, { method: 'POST', body: request, idempotencyKey }),

  messages: (shopId: string, query: { docId?: string; status?: string; channel?: string; limit?: number; cursor?: string } = {}) =>
    authedPosApi<{ items: MessageRow[]; nextCursor: string | null }>(shopId, '/messages', { query: query as any }),

  credits: (shopId: string) => authedPosApi<Credits>(shopId, '/messages/credits'),

  retryMessage: (shopId: string, messageId: string) =>
    authedPosApi<SendResult>(shopId, `/messages/${encodeURIComponent(messageId)}/retry`, { method: 'POST', body: {} }),

  void: (shopId: string, docId: string, reason: string) =>
    authedPosApi<{ document: InvoiceDoc }>(shopId, `${doc(docId)}/void`, { method: 'POST', body: { reason } }),

  duplicate: (shopId: string, docId: string, docType?: 'INVOICE' | 'QUOTATION') =>
    authedPosApi<{ document: InvoiceDoc }>(shopId, `${doc(docId)}/duplicate`, { method: 'POST', body: docType ? { docType } : {} }),

  creditNote: (shopId: string, docId: string, input: CreditNoteInput) =>
    authedPosApi<{ creditNote: InvoiceDoc; document: InvoiceDoc }>(shopId, `${doc(docId)}/credit-notes`, { method: 'POST', body: input }),

  recordPayment: (shopId: string, docId: string, input: PaymentInput, idempotencyKey: string = newKey()) =>
    authedPosApi<{ payment: Payment; document: InvoiceDoc; receipt: InvoiceDoc }>(shopId, `${doc(docId)}/payments`, { method: 'POST', body: input, idempotencyKey }),

  pdfUrl: (shopId: string, docId: string, size: 'A4' | '80mm' = 'A4', copy: 'ORIGINAL' | 'DUPLICATE' = 'ORIGINAL') =>
    authedPosApi<{ url: string; expiresAt: number }>(shopId, `${doc(docId)}/pdf`, { query: { size, copy } }),

  regenerateLink: (shopId: string, docId: string) =>
    authedPosApi<{ shareCode: string; shareUrl: string }>(shopId, `${doc(docId)}/share-link/regenerate`, { method: 'POST', body: {} }),

  setRemindersPaused: (shopId: string, docId: string, paused: boolean) =>
    authedPosApi<{ document: InvoiceDoc }>(shopId, `${doc(docId)}/reminders`, { method: 'POST', body: { paused } }),

  shareMessage: (shopId: string, docId: string, query: { channel?: string; language?: string; templateKey?: string } = {}) =>
    authedPosApi<ShareMessage>(shopId, `${doc(docId)}/share-message`, { query }),

  shareEvent: (shopId: string, docId: string, channel: ManualChannel) =>
    authedPosApi<{ event: InvoiceEvent }>(shopId, `${doc(docId)}/share-events`, { method: 'POST', body: { channel } }),

  pendingClaims: (shopId: string, query: { method?: string; clientId?: string } = {}) =>
    authedPosApi<{ items: PendingClaim[]; count: number; totalMinor: number }>(shopId, '/invoices/payments/pending', { query }),

  approveClaim: (shopId: string, paymentId: string, amountMinor?: number) =>
    authedPosApi<{ payment: Payment; document: InvoiceDoc; receipt: InvoiceDoc }>(shopId, `/invoices/payments/${encodeURIComponent(paymentId)}/approve`, { method: 'POST', body: amountMinor ? { amountMinor } : {} }),

  rejectClaim: (shopId: string, paymentId: string, reasonCode: RejectCode, reason?: string) =>
    authedPosApi<{ payment: Payment; document: InvoiceDoc }>(shopId, `/invoices/payments/${encodeURIComponent(paymentId)}/reject`, { method: 'POST', body: { reasonCode, reason } }),

  approveBulk: (shopId: string, paymentIds: string[]) =>
    authedPosApi<{ approved: { paymentId: string; docId: string }[]; failed: { paymentId: string; code: string }[] }>(shopId, '/invoices/payments/approve-bulk', { method: 'POST', body: { paymentIds } }),

  /** Quotation → invoice; with issue = true the invoice is issued at once. */
  convert: (shopId: string, docId: string, issue = false) =>
    authedPosApi<{ document: InvoiceDoc }>(shopId, `${doc(docId)}/convert`, { method: 'POST', body: { issue } }),

  recurring: (shopId: string) => authedPosApi<{ items: Recurring[] }>(shopId, '/invoices/recurring'),

  createRecurring: (shopId: string, input: RecurringInput) =>
    authedPosApi<{ recurring: Recurring }>(shopId, '/invoices/recurring', { method: 'POST', body: input }),

  updateRecurring: (shopId: string, id: string, patch: Partial<RecurringInput> & { status?: 'ACTIVE' | 'PAUSED' }) =>
    authedPosApi<{ recurring: Recurring }>(shopId, `/invoices/recurring/${encodeURIComponent(id)}`, { method: 'PUT', body: patch }),

  endRecurring: (shopId: string, id: string) =>
    authedPosApi<null>(shopId, `/invoices/recurring/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  runRecurring: (shopId: string, id: string) =>
    authedPosApi<{ document: InvoiceDoc }>(shopId, `/invoices/recurring/${encodeURIComponent(id)}/run`, { method: 'POST', body: {} }),

  statement: (shopId: string, clientId: string, from?: string, to?: string) =>
    authedPosApi<Statement>(shopId, `/invoices/customers/${encodeURIComponent(clientId)}/statement`, { query: { from, to } }),

  fromOrder: (shopId: string, orderId: string, mode: FromOrderMode, clientId?: string) =>
    authedPosApi<FromOrderResult>(shopId, '/invoices/from-order', { method: 'POST', body: { orderId, mode, ...(clientId ? { clientId } : {}) } }),

  openInvoices: (shopId: string, query: { clientId?: string; code?: string }) =>
    authedPosApi<{ items: InvoiceListItem[] }>(shopId, '/invoices/open', { query }),

  collections: (shopId: string, query: { closeCashId?: string; from?: string; to?: string }) =>
    authedPosApi<CollectionsResult>(shopId, '/invoices/collections', { query }),

  customerBalances: (shopId: string) =>
    authedPosApi<{ items: (CustomerBalance & { clientId: string })[] }>(shopId, '/invoices/customers/balances'),

  customerBalance: (shopId: string, clientId: string) =>
    authedPosApi<CustomerBalance>(shopId, `/invoices/customers/${encodeURIComponent(clientId)}/balance`),

  setCreditLimit: (shopId: string, clientId: string, creditLimitMinor: number | null) =>
    authedPosApi<CustomerBalance>(shopId, `/invoices/customers/${encodeURIComponent(clientId)}/profile`, { method: 'PUT', body: { creditLimitMinor } }),

  getSettings: (shopId: string) => authedPosApi<{ settings: InvoiceSettings & { templateDefaults?: TemplateDefaults } }>(shopId, '/invoice-settings'),

  saveSettings: (shopId: string, patch: Partial<InvoiceSettings> & Record<string, unknown>) =>
    authedPosApi<{ settings: InvoiceSettings }>(shopId, '/invoice-settings', { method: 'PUT', body: patch }),
};

export default InvoiceRepository;
